// src/data/insights/insightsRegistry.ts
//
// ─── HOW TO ADD A NEW ARTICLE ─────────────────────────────────────────────
//
// 1. Create the JSON file:  src/data/insights/your-slug.json
//    (Follow the same shape as existing articles — see typescript-monorepos-fintech.json)
//
// 2. Import it here:
//    import yourArticle from "./your-slug.json";
//
// 3. Add it to INSIGHTS_REGISTRY at the TOP of the array (newest first):
//    {
//      ...articleMeta(yourArticle),
//      image: "/images/your-cover.jpg",   // optional — falls back to heroImage
//      featured: false,
//    },
//
// That's it. Home.tsx and Insights.tsx will both update automatically.
// ─────────────────────────────────────────────────────────────────────────────

import typescriptMonoreposFintech from "./typescript-monorepos-fintech.json";
import lowBandwidthUx from "./low-bandwidth-ux.json";
import sprintFramework from "./90-day-sprint-framework.json";
import launchKitLessons from "./launch-kit-naija-lessons.json";
import nigerianDesignSystem from "./nigerian-design-system.json";
import rtkQueryPatterns from "./rtk-query-patterns.json";

// ─── Shared article type ──────────────────────────────────────────────────

export interface ArticleMeta {
  /** URL slug — must match the JSON filename exactly */
  slug: string;
  tag: string;
  date: string;
  readTime: string;
  title: string;
  excerpt: string;
  /** Cover image shown on cards. Falls back to heroImage from JSON if omitted */
  image: string;
  /** Author name only — used for card attribution if needed */
  authorName: string;
  /** Set true for exactly ONE article — the featured hero on the Insights page */
  featured: boolean;
  href: string;
}

// ─── Helper: lift shared metadata out of a JSON import ────────────────────

function articleMeta(json: {
  slug: string;
  tag: string;
  date: string;
  readTime: string;
  title: string;
  excerpt: string;
  heroImage: string;
  author: { name: string };
}): Omit<ArticleMeta, "featured" | "image"> & { image: string } {
  return {
    slug: json.slug,
    tag: json.tag,
    date: json.date,
    readTime: json.readTime,
    title: json.title,
    excerpt: json.excerpt,
    image: json.heroImage,
    authorName: json.author.name,
    href: `/insights/${json.slug}`,
  };
}

// ─── Registry ─────────────────────────────────────────────────────────────
// Order = newest first. Only one entry should have featured: true.

export const INSIGHTS_REGISTRY: ArticleMeta[] = [
  {
    ...articleMeta(typescriptMonoreposFintech),
    featured: true, // ← change this when a newer article becomes featured
  },
  {
    ...articleMeta(lowBandwidthUx),
    featured: false,
  },
  {
    ...articleMeta(sprintFramework),
    featured: false,
  },
  {
    ...articleMeta(launchKitLessons),
    featured: false,
  },
  {
    ...articleMeta(nigerianDesignSystem),
    featured: false,
  },
  {
    ...articleMeta(rtkQueryPatterns),
    featured: false,
  },
];

// ─── Derived views (used by Home.tsx and Insights.tsx) ────────────────────

/** The single featured article — shown as the hero card on the Insights page */
export const FEATURED_ARTICLE = INSIGHTS_REGISTRY.find((a) => a.featured)!;

/** All non-featured articles — used in the grid */
export const GRID_ARTICLES = INSIGHTS_REGISTRY.filter((a) => !a.featured);

/** The 4 most recent articles — used on the Home page insights strip */
export const HOME_ARTICLES = INSIGHTS_REGISTRY.slice(0, 4);

/** Lookup by slug — used by SingleInsight to find related articles */
export const ARTICLE_BY_SLUG: Record<string, ArticleMeta> = Object.fromEntries(
  INSIGHTS_REGISTRY.map((a) => [a.slug, a]),
);
